import { captureException } from '@sentry/nextjs';
import { isJsonString } from './utilsService';

type RequestMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
type RequestParams = {
  method: RequestMethod;
  token?: string;
  body?: string | FormData;
  hasContentType?: boolean;
};

type RequestConfig = {
  body?: string | FormData;
  method: RequestMethod;
  headers: Headers;
};

export function getRequestConfig({
  method,
  token,
  body,
  hasContentType = true,
}: RequestParams): RequestConfig {
  return {
    method,
    headers: new Headers({
      ...(hasContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    ...(body ? { body } : {}),
  };
}

export function handleResponse(response: Response): Promise<any> {
  return response.text().then((text) => {
    const data = isJsonString(text) ? JSON.parse(text) : '';
    if (!response.ok) {
      if (data.errors) {
        const parsedServerError = data;
        let sentryMessage = '';
        for (let i = 0; i < data.errors.length; i += 1) {
          let message = '';
          switch (data.errors[i].code.toString()) {
            case '0001':
              message = 'validationError';
              break;
            case '0002':
              message = 'entityNotFound';
              break;
            case '0003':
              message = 'providedAddressInvalid';
              break;
            case '0004':
              message = 'failedConvertAddress';
              break;
            case '0005':
              message = 'failedVerifySignature';
              break;
            case '0006':
              message = 'multisigAddressUnsupported';
              break;
            case '0007':
              message = 'unauthorized';
              break;
            case '0008':
              message = 'dealReviewAlreadyExistsTryPatchInstead';
              break;
            default:
              if (data.errors[i].message) {
                message = data.errors[i].message;
              } else {
                sentryMessage += `Code:${data.errors[i].code}\n`;
              }
          }
          parsedServerError.errors[i].message = message;
        }
        if (sentryMessage)
          captureException({ message: sentryMessage, traceId: data.traceId });

        return Promise.reject(
          new Error(parsedServerError.message || JSON.stringify(parsedServerError)),
        );
      }

      captureException({ message: text, traceId: data.traceId });

      return Promise.reject(new Error(text));
    }

    // TODO - Niko - Handle standardized errors from the server (map codes to a message)

    return data;
  });
}

export function handleResponseAirTable(response: Response): Promise<any> {
  return response.text().then((text) => {
    const data = isJsonString(text) ? JSON.parse(text) : '';
    if (!response.ok) {
      if (data.error && data.error.message) {
        captureException({ message: data.error.message });

        return Promise.reject(new Error(data.error.message));
      }

      captureException({ message: text });

      return Promise.reject(new Error(text));
    }

    return data;
  });
}

export function handleResponseCryptoCompare(response: Response): Promise<any> {
  return response.text().then((text) => {
    const data = isJsonString(text) ? JSON.parse(text) : '';
    if (!response.ok || (data.Response && data.Response === 'Error')) {
      if (data.Message) {
        captureException({ message: data.Message });

        return Promise.reject(new Error(data.Message));
      }

      captureException({ message: text });

      return Promise.reject(new Error(text));
    }

    return data;
  });
}
